import { createFileRoute, Link } from '@tanstack/react-router';

import { Button, Card, PageLayout } from '@keboola/design';
import { BookOpen, PencilLine, Send, UserPlus } from '@keboola/design/icons';

import { TextLink } from '../../components/TextLink';
import { AppCard } from '../../features/apps/components/AppCard';
import { AppIcon } from '../../features/apps/components/AppIcon';
import { FEATURED_APPS } from '../../features/apps/constants';
import { filterApps } from '../../features/apps/helpers';
import { usePublishedApps } from '../../features/apps/queries';
import { pickWallIcons } from '../../features/apps/wall';

export const Route = createFileRoute('/_public/')({
  component: HomePage,
  head: () => ({ meta: [{ title: 'Keboola Developer Portal' }] }),
});

const STEPS = [
  { icon: UserPlus, title: 'Create an account', text: 'We will confirm your account and vendor' },
  {
    icon: BookOpen,
    title: 'Explore Resources & API',
    text: 'Learn more about how to make a Keboola component',
    to: '/resources',
  },
  { icon: PencilLine, title: 'Create & upload component', text: 'Use our resources and API' },
  {
    icon: Send,
    title: 'Make your component public',
    text: 'We will check your component before it goes public',
  },
] as const;

const WALL_SIZE = 28;

/**
 * A band of real component icons under the hero.
 *
 * - ⚠️ Counts `filterApps` output, not `apps.length`: the count must match what `/components` lists.
 * - Fetches the full catalog (~505 kB gzipped) on `/` deliberately; it warms the main CTA's click.
 * - No skeleton: it is decoration, and `aria-hidden` because every icon is labelled one click away.
 */
function ComponentWall() {
  const { data: apps } = usePublishedApps();

  if (apps === undefined) return null;

  const catalog = filterApps(apps, {
    query: '',
    type: null,
    categories: [],
    stage: null,
    keboolaOnly: false,
  });
  if (catalog.length === 0) return null;

  const wall = pickWallIcons(catalog, WALL_SIZE);

  return (
    <div className="tw:mt-8 tw:flex tw:flex-col tw:items-center tw:gap-4">
      <div
        aria-hidden="true"
        className="tw:flex tw:flex-wrap tw:justify-center tw:gap-3 tw:opacity-90"
      >
        {wall.map((app) => (
          <AppIcon key={app.id} app={app} size={48} />
        ))}
      </div>
      <p className="tw:m-0 tw:text-sm tw:text-muted-foreground">
        <strong className="tw:font-semibold tw:text-foreground">{catalog.length} components</strong>{' '}
        published to the platform, and counting.{' '}
        <TextLink to="/components">Browse them all</TextLink>
      </p>
    </div>
  );
}

/**
 * The hero, on `bg-accent` — the only surface that holds contrast in every brand × scheme.
 *
 * - `bg-brand`: without `theme.gradient` the sweep ends near-white and hides a light headline.
 * - `bg-primary`: a brand without `colorsDark` falls back to the design system's blue.
 */
function Hero() {
  return (
    <section className="tw:overflow-hidden tw:rounded-lg tw:border tw:border-border tw:bg-accent tw:text-accent-foreground tw:shadow-lift">
      <div aria-hidden="true" className="tw:h-1.5 tw:bg-brand" />

      <div className="tw:px-6 tw:py-12 tw:sm:px-12 tw:sm:py-16">
        <div className="tw:max-w-3xl">
          {/* `Card.Title`: `design/no-raw-heading-on-page` reserves raw h1/h2 for `PageHeader`. */}
          {/* ⚠️ `text-current` is load-bearing: `Card.Title`'s own colour fails contrast here. */}
          <Card.Title
            as="h1"
            className="tw:m-0 tw:text-4xl tw:leading-tight tw:font-bold tw:text-current tw:sm:text-5xl"
          >
            Build a component. Reach every Keboola project.
          </Card.Title>
          <p className="tw:mt-4 tw:mb-0 tw:text-lg tw:opacity-90">
            Publish to a platform that already moves data for hundreds of companies. Your component
            gets the connectors, the scheduling, the credentials and the monitoring for free — you
            write the part only you can write.
          </p>

          <div className="tw:mt-8 tw:flex tw:flex-wrap tw:items-center tw:gap-3">
            <Button asChild variant="primary" size="large">
              <Link to="/auth/create-account">Create an account</Link>
            </Button>
            {/* ⚠️ A real `Button`, so size and brand-mapped radius match the primary beside it. */}
            <Button asChild variant="outline" size="large">
              <Link to="/components">Browse components</Link>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}

/**
 * Section headings are `Card.Title as="h2"` outside a `Card` on purpose.
 *
 * - `design/no-raw-heading-on-page` reserves raw h1/h2 for `PageHeader`.
 * - A card around a grid of cards reads as nested chrome.
 */
function HomePage() {
  return (
    <PageLayout>
      <Hero />
      <ComponentWall />

      <section aria-labelledby="how-it-works" className="tw:my-16">
        <Card.Title as="h2" id="how-it-works" className="tw:mb-6">
          How it works
        </Card.Title>
        <ol className="tw:m-0 tw:grid tw:list-none tw:grid-cols-1 tw:gap-4 tw:p-0 tw:sm:grid-cols-2 tw:lg:grid-cols-4">
          {STEPS.map((step, index) => (
            <li
              key={step.title}
              className="tw:flex tw:flex-col tw:gap-2 tw:rounded-lg tw:border tw:border-border tw:bg-card tw:p-5"
            >
              {/* `bg-accent` needs `text-accent-foreground`, or the glyph keeps the body ink. */}
              <div className="tw:flex tw:size-10 tw:items-center tw:justify-center tw:rounded-full tw:bg-accent tw:text-accent-foreground">
                <step.icon />
              </div>
              <h3 className="tw:m-0 tw:text-base tw:font-semibold tw:text-foreground">
                <span className="tw:text-muted-foreground">{index + 1}. </span>
                {step.title}
              </h3>
              <p className="tw:m-0 tw:text-sm tw:text-muted-foreground">
                {'to' in step ? <TextLink to={step.to}>{step.text}</TextLink> : step.text}
              </p>
            </li>
          ))}
        </ol>
      </section>

      <section aria-labelledby="featured">
        <Card.Title as="h2" id="featured" className="tw:mb-6">
          Hardest working components
        </Card.Title>
        <div className="tw:grid tw:grid-cols-1 tw:gap-4 tw:sm:grid-cols-2 tw:lg:grid-cols-3">
          {FEATURED_APPS.map((app) => (
            // These entries carry no `type`.
            <AppCard key={app.id} app={app} showType={false} />
          ))}
        </div>
        <div className="tw:mt-8 tw:text-center">
          <Button asChild variant="secondary" size="large">
            <Link to="/components">More components</Link>
          </Button>
        </div>
      </section>

      {/* Same surface reasoning as the hero. */}
      <section
        aria-labelledby="get-started"
        className="tw:mt-16 tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-6 tw:rounded-lg tw:border tw:border-border tw:bg-accent tw:px-6 tw:py-10 tw:text-accent-foreground tw:sm:px-10"
      >
        <div className="tw:max-w-xl">
          <Card.Title
            as="h2"
            id="get-started"
            className="tw:m-0 tw:text-2xl tw:font-bold tw:text-current"
          >
            Ready to publish?
          </Card.Title>
          <p className="tw:mt-2 tw:mb-0 tw:opacity-90">
            Registration takes a minute. We confirm your vendor, and the API and tutorials are open
            to read before you commit to anything.
          </p>
        </div>
        <div className="tw:flex tw:flex-wrap tw:gap-3">
          <Button asChild variant="primary" size="large">
            <Link to="/auth/create-account">Create an account</Link>
          </Button>
          <Button asChild variant="outline" size="large">
            <Link to="/resources">Read the docs</Link>
          </Button>
        </div>
      </section>
    </PageLayout>
  );
}
